// /tenant/maintenance — list + create + tenant↔landlord messaging
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import TenantShell from "@/components/TenantShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Wrench, Loader2, X, Send, MessageSquare, Upload } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["plumbing", "electrical", "hvac", "appliance", "structural", "cosmetic", "landscaping", "pest", "other"];

function StatusPill({ status }) {
  const map = { open: "bg-amber-100 text-amber-700", in_progress: "bg-indigo-100 text-indigo-700", resolved: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-100 text-slate-500" };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map[status] || "bg-slate-100"}`}>{(status || "").replace("_"," ")}</span>;
}

function PriorityPill({ p }) {
  const map = { emergency: "text-red-700 bg-red-50 border-red-200", high: "text-orange-700 bg-orange-50 border-orange-200", medium: "text-amber-700 bg-amber-50 border-amber-200", low: "text-slate-600 bg-slate-50 border-slate-200" };
  return <span className={`rounded-full border px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider ${map[p] || map.low}`}>{p}</span>;
}

export default function TenantMaintenance() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [thread, setThread] = useState(null);
  const [open, setOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(params.get("msg") === "1");
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "plumbing", priority: "medium" });
  const [saving, setSaving] = useState(false);
  const [msgInput, setMsgInput] = useState("");

  const load = async () => {
    const [{ data: ms }, { data: th }] = await Promise.all([
      api.get("/tenant/maintenance"),
      api.get("/tenant/messages/thread"),
    ]);
    setItems(ms);
    setThread(th.thread);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const { data: m } = await api.post("/tenant/maintenance", form);
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await api.post(`/tenant/maintenance/${m.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Request submitted");
      setOpen(false);
      setForm({ title: "", description: "", category: "plumbing", priority: "medium" });
      setFiles([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const sendMsg = async () => {
    if (!msgInput.trim()) return;
    try {
      const { data } = await api.post("/tenant/messages/thread/messages", { content: msgInput });
      setThread(data);
      setMsgInput("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    }
  };

  const removeItem = async (mid) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/tenant/maintenance/${mid}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <TenantShell>
      <div className="space-y-6" data-testid="tenant-maintenance-page">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Maintenance</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Maintenance Requests</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMsgOpen(true)} className="border-slate-200" data-testid="tenant-open-messages">
              <MessageSquare className="mr-2 h-4 w-4" /> Message landlord
            </Button>
            <Button onClick={() => setOpen(true)} className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="tenant-new-maint">
              <Plus className="mr-2 h-4 w-4" /> New request
            </Button>
          </div>
        </header>

        {items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="tenant-maint-empty">No requests yet. Tap "New request" to report an issue.</p>
        ) : (
          <ul className="space-y-3" data-testid="tenant-maint-list">
            {items.map((m) => (
              <li key={m.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`tenant-maint-${m.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <PriorityPill p={m.priority} />
                    <p className="text-base font-semibold text-slate-900">{m.title}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={m.status} />
                    {m.status === "open" && (
                      <Button variant="ghost" size="sm" onClick={() => removeItem(m.id)} className="text-red-600">
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {m.description && <p className="mt-2 text-sm text-slate-600">{m.description}</p>}
                {m.photo_signed_urls?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`tenant-maint-photos-${m.id}`}>
                    {m.photo_signed_urls.map((u, i) => u && (
                      <a key={u} href={u} target="_blank" rel="noopener noreferrer">
                        <img src={u} alt={`Maintenance photo ${i + 1}`} className="h-16 w-16 rounded-md border border-slate-200 object-cover" />
                      </a>
                    ))}
                  </div>
                )}
                <p className="mt-3 text-[11px] text-slate-400">Reported {new Date(m.reported_at).toLocaleDateString()} · {m.category}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-slate-200 bg-white" data-testid="tenant-new-maint-modal">
          <DialogHeader>
            <DialogTitle>New maintenance request</DialogTitle>
            <DialogDescription className="text-slate-500">For emergencies (no water/heat, fire, flood), call your landlord directly.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="t">Title *</Label>
              <Input id="t" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="tenant-maint-title" placeholder="e.g. Kitchen sink leaking" />
            </div>
            <div>
              <Label htmlFor="d">Description</Label>
              <Textarea id="d" rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="tenant-maint-description" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="tenant-maint-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger data-testid="tenant-maint-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Photos (up to 5)</Label>
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
                className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-indigo-700 hover:file:bg-indigo-100"
                data-testid="tenant-maint-photos" />
              {files.length > 0 && <p className="mt-1 text-xs text-slate-500">{files.length} photo{files.length !== 1 ? "s" : ""} selected</p>}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
              <Button type="submit" className="bg-indigo-600 text-white hover:bg-indigo-700" disabled={saving} data-testid="tenant-maint-submit">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wrench className="mr-2 h-4 w-4" />}
                Submit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="max-w-md border-slate-200 bg-white" data-testid="tenant-msg-modal">
          <DialogHeader>
            <DialogTitle>Message your landlord</DialogTitle>
            <DialogDescription className="text-slate-500">No AI — your landlord reads and replies directly.</DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto rounded-md border border-slate-100 bg-slate-50 p-3" data-testid="tenant-msg-thread">
            {!thread || !thread.messages?.length ? (
              <p className="text-center text-xs text-slate-400">No messages yet — say hi.</p>
            ) : (
              <ul className="space-y-2">
                {thread.messages.map((m, i) => (
                  <li key={`${m.created_at}-${i}`} className={`flex ${m.role === "tenant" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === "tenant" ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                      <p>{m.content}</p>
                      <p className={`mt-0.5 text-[9px] ${m.role === "tenant" ? "text-indigo-200" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex gap-2">
            <Textarea rows={2} value={msgInput} onChange={(e) => setMsgInput(e.target.value)} placeholder="Type your message…" data-testid="tenant-msg-input" />
            <Button onClick={sendMsg} className="self-end bg-indigo-600 text-white hover:bg-indigo-700" data-testid="tenant-msg-send"><Send className="h-4 w-4" /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </TenantShell>
  );
}
